import React, { useRef, useState } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FileEarmarkPdfFill } from 'react-bootstrap-icons';
import { Upload, Eye, Trash2 } from 'lucide-react';
import AlertModal from '../../AlertModal';
import { makeRESTApiRequest } from '../../../helpers/api';
import { API_URL, ENDPOINTS } from '../../../helpers/constants';
import { toast } from 'sonner';

const FileAttachmentsModal = ({ show, onHide, step, onFilesChange }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteCandidateFileId, setDeleteCandidateFileId] = useState(null);

  const files = step?.files || [];
  const scopeLabel = step?.scope === 'county' ? 'County' : 'Municipality';

  const handleFiles = async fileList => {
    const file = fileList?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      return;
    }

    if (!step?._id) {
      toast.error('Invalid step');
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scope', step.scope);

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INSTRUCTION_BUILDER_STEP_FILES(step._id),
        method: 'POST',
        requestBody: formData,
        contentType: 'multipart/form-data',
        stringify: false
      });

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('File uploaded successfully');
      onFilesChange?.(step._id, response?.data?.files || []);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = e => {
    const list = e.target.files;
    handleFiles(list);
    e.target.value = '';
  };

  const handlePreviewFile = async fileId => {
    if (!step?._id) {
      toast.error('Invalid step');
      return;
    }

    const token = localStorage.getItem('user-token');
    const url = `${API_URL}${ENDPOINTS.INSTRUCTION_BUILDER_STEP_FILE_PREVIEW(
      step._id,
      fileId,
      'stream'
    )}&scope=${encodeURIComponent(step.scope)}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      if (!response.ok) {
        toast.error('Failed to preview file');
        return;
      }

      const blob = await response.blob();
      const previewUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error previewing file:', err);
      toast.error('Failed to preview file');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidateFileId || !step?._id) return;

    const fileId = deleteCandidateFileId;
    setDeleteCandidateFileId(null);

    try {
      const { error } = await makeRESTApiRequest({
        endpoint: `${ENDPOINTS.INSTRUCTION_BUILDER_STEP_FILE(step._id, fileId)}?scope=${step.scope}`,
        method: 'DELETE'
      });

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('File deleted successfully');
      onFilesChange?.(
        step._id,
        files.filter(file => String(file?._id || file) !== String(fileId))
      );
    } catch (err) {
      console.error('Error deleting file:', err);
      toast.error('Failed to delete file');
    }
  };

  if (!step) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>
          {step.title || 'Step'}: {scopeLabel} Attachments{' '}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          padding: '20px',
          maxHeight: 'min(70vh, calc(100dvh - 200px))',
          overflowY: 'auto'
        }}
      >
        <div
          onDragOver={e => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setIsDragOver(false);
            handleFiles(e.dataTransfer?.files);
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragOver ? '#16a34a' : '#d1d5db'}`,
            borderRadius: '10px',
            padding: '20px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            background: isDragOver ? '#f0fdf4' : '#fafafa',
            opacity: uploading ? 0.75 : 1
          }}
        >
          <Upload size={20} className="mb-2" />
          <div style={{ fontSize: '14px', fontWeight: 500 }}>Drag and drop PDF here</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>or click the upload icon area to choose file</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileInputChange}
            className="d-none"
            disabled={uploading}
          />
        </div>

        <small className="text-muted mt-2 d-block">Only PDF files are supported.</small>

        {selectedFile && (
          <div
            className="position-relative mt-3 p-2 text-dark rounded-lg border d-flex align-items-center bg-primary-light"
            style={{ overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            <FileEarmarkPdfFill size={16} className="text-danger flex-shrink-0 ml-1" />
            <div className="d-flex flex-column tinyFont ml-2" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
                title={selectedFile.name}
              >
                {selectedFile.name}
              </span>
            </div>
            {uploading && <small className="text-muted">Uploading...</small>}
          </div>
        )}

        {files.length > 0 && (
          <div className="mt-3">
            <small className="text-muted d-block mb-2">{scopeLabel} uploaded files</small>
            <div className="d-flex flex-column" style={{ gap: '6px' }}>
              {files.map(file => {
                const fileId = String(file?._id || file);
                const fileLabel = file?.metadata?.originalName || file?.filename || `File ${fileId.slice(-6)}`;

                return (
                  <OverlayTrigger
                    key={fileId}
                    delay={{ show: 400, hide: 10 }}
                    placement="bottom"
                    overlay={
                      <Tooltip id={`uploaded-pdf-tt-${fileId}`} className="tinyFont">
                        {fileLabel}
                      </Tooltip>
                    }
                  >
                    <div
                      className="position-relative text-dark rounded-lg d-flex align-items-center justify-content-between p-1 border"
                      style={{
                        background: '#fff',
                        minHeight: '36px',
                        paddingLeft: '4px',
                        paddingRight: '4px'
                      }}
                    >
                      <div
                        className="d-flex align-items-center flex-grow-1 pl-1"
                        style={{ minWidth: 0, overflow: 'hidden' }}
                      >
                        <FileEarmarkPdfFill size={16} className="text-danger flex-shrink-0" />
                        <div
                          className="tinyFont ml-2"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1
                          }}
                        >
                          {fileLabel}
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '4px' }}>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handlePreviewFile(fileId);
                          }}
                          className="p-1 border-0 d-flex align-items-center justify-content-center"
                          style={{ minWidth: '32px' }}
                          title="Preview file"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            setDeleteCandidateFileId(fileId);
                          }}
                          className="p-1 text-danger border-0 d-flex align-items-center justify-content-center"
                          style={{ minWidth: '32px' }}
                          title="Delete file"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </OverlayTrigger>
                );
              })}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={uploading}>
          Cancel
        </Button>
      </Modal.Footer>

      <AlertModal
        show={deleteCandidateFileId !== null}
        onHide={() => setDeleteCandidateFileId(null)}
        onDismissClick={() => setDeleteCandidateFileId(null)}
        onContinueClick={handleConfirmDelete}
        alertText="Are you sure you want to delete this template file from this step?"
        dismissButtonText="Cancel"
        continueButtonText="Delete"
        dismissButtonVariant="secondary"
        continueButtonVariant="danger"
      />
    </Modal>
  );
};

export default FileAttachmentsModal;
